import React, { useState, Component } from 'react';
import { Camera, Info, X, Check, CheckCircle, Clock, PlusCircle, Calendar, Package, Activity, Eye, Sparkles, XCircle, AlertTriangle, MinusCircle, MessageSquare } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/Button';
import { Calendar as CalendarComponent } from '../components/Calendar';
import { Logo } from '../components/Logo';
import { Badge } from '../components/Badge';
// Curated placeholder images for prototype
const PLACEHOLDER_IMAGES = [{
  url: 'https://images.unsplash.com/photo-1504148455328-c376907d081c?w=400&q=80',
  label: 'Power Tools'
}, {
  url: 'https://images.unsplash.com/photo-1572981779307-38b8cabb2407?w=400&q=80',
  label: 'Drill Set'
}, {
  url: 'https://images.unsplash.com/photo-1530124566582-a45a7e3e29f0?w=400&q=80',
  label: 'Camping Gear'
}, {
  url: 'https://images.unsplash.com/photo-1504280390367-361c6d9f38f4?w=400&q=80',
  label: 'Tent'
}, {
  url: 'https://images.unsplash.com/photo-1519340241574-2cec6aef0c01?w=400&q=80',
  label: 'Baby Stroller'
}, {
  url: 'https://images.unsplash.com/photo-1505236858219-8359eb29e329?w=400&q=80',
  label: 'Party Supplies'
}, {
  url: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=400&q=80',
  label: 'Kitchen Mixer'
}, {
  url: 'https://images.unsplash.com/photo-1558618666-fcd25c85f82e?w=400&q=80',
  label: 'Lawn Mower'
}, {
  url: 'https://images.unsplash.com/photo-1461896836934-bd45ba8fcf9b?w=400&q=80',
  label: 'Bicycle'
}, {
  url: 'https://images.unsplash.com/photo-1526401485004-46910ecc8e51?w=400&q=80',
  label: 'Kayak'
}, {
  url: 'https://images.unsplash.com/photo-1585771724684-38269d6639fd?w=400&q=80',
  label: 'Board Games'
}, {
  url: 'https://images.unsplash.com/photo-1593642632559-0c6d3fc62b89?w=400&q=80',
  label: 'Electronics'
}];
type ListingStatus = 'form' | 'under-review' | 'approved' | 'denied' | 'needs-info';
interface CreateListingProps {
  onBack: () => void;
  onComplete: () => void;
  onListAnother?: () => void;
  onViewListings?: () => void;
  onMyActivity?: () => void;
}
export function CreateListing({
  onBack,
  onComplete,
  onListAnother,
  onViewListings,
  onMyActivity
}: CreateListingProps) {
  const [photos, setPhotos] = useState<string[]>([]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [dailyRate, setDailyRate] = useState('');
  const [deposit, setDeposit] = useState('');
  const [blockedDates, setBlockedDates] = useState<Date[]>([]);
  const [showDepositInfo, setShowDepositInfo] = useState(false);
  const [showPhotoPicker, setShowPhotoPicker] = useState(false);
  const [listingStatus, setListingStatus] = useState<ListingStatus>('form');
  const [reviewResponse, setReviewResponse] = useState('');
  const [responseSubmitted, setResponseSubmitted] = useState(false);
  const categories = ['Tools', 'Baby Gear', 'Party', 'Outdoors', 'Sports', 'Electronics', 'Kitchen', 'Lawn Care'];
  // Calculate display price (daily rate + 25%)
  const displayPrice = dailyRate ? (parseFloat(dailyRate) * 1.25).toFixed(2) : '0.00';
  const handleCategoryToggle = (cat: string) => {
    if (selectedCategories.includes(cat)) {
      setSelectedCategories(selectedCategories.filter(c => c !== cat));
    } else if (selectedCategories.length < 2) {
      setSelectedCategories([...selectedCategories, cat]);
    }
  };
  const handleSelectPlaceholder = (url: string) => {
    if (photos.includes(url)) {
      setPhotos(photos.filter(p => p !== url));
    } else if (photos.length < 5) {
      setPhotos([...photos, url]);
    }
  };
  const handleRemovePhoto = (index: number) => {
    setPhotos(photos.filter((_, i) => i !== index));
  };
  const handleSubmit = () => {
    setListingStatus('under-review');
  };
  const handleListAnother = () => {
    setPhotos([]);
    setTitle('');
    setDescription('');
    setSelectedCategories([]);
    setDailyRate('');
    setDeposit('');
    setBlockedDates([]);
    setListingStatus('form');
    if (onListAnother) onListAnother();
  };
  const handleSubmitResponse = () => {
    setResponseSubmitted(true);
    // After submitting response, go back to under-review
    setTimeout(() => {
      setReviewResponse('');
      setResponseSubmitted(false);
      setListingStatus('under-review');
    }, 1500);
  };
  const remainingSlots = 5 - photos.length;
  // ─── Shared Header ───
  const ListingHeader = () => <div className="bg-white sticky top-0 z-20" data-id="element-837">
      <div className="px-3 pt-2.5" data-id="element-838">
        <Logo variant="full" size="sm" data-id="element-839" />
      </div>
      <div className="px-3 pt-3 pb-2" data-id="element-840">
        <h1 className="text-lg font-semibold text-charcoal text-center" data-id="element-841">
          Create Listing
        </h1>
      </div>
      <div className="h-px bg-sand w-full" data-id="element-842" />
    </div>;
  // ─── Shared Item Preview Card ───
  const ItemPreviewCard = ({
    delay = 0.3
  }: {
    delay?: number;
  }) => <motion.div initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay
  }} className="mt-3 bg-white border border-sand rounded-lg p-3" style={{
    minHeight: '100px'
  }} data-id="element-843">
      <div className="flex gap-3" data-id="element-844">
        {photos.length > 0 ? <img src={photos[0]} alt={title} className="w-20 h-20 rounded-md object-cover flex-shrink-0" data-id="element-845" /> : <div className="w-20 h-20 rounded-md bg-sand/30 flex items-center justify-center flex-shrink-0" data-id="element-846">
            <Camera size={24} className="text-moss-gray" data-id="element-847" />
          </div>}
        <div className="flex-1 min-w-0" data-id="element-848">
          <h3 className="text-sm font-bold text-charcoal truncate" data-id="element-849">
            {title || 'Untitled Item'}
          </h3>
          {selectedCategories.length > 0 && <div className="flex gap-1 mt-1" data-id="element-850">
              {selectedCategories.map(cat => <span key={cat} className="text-[10px] text-charcoal/50 bg-sand/40 px-1.5 py-0.5 rounded" data-id="element-851">
                  {cat}
                </span>)}
            </div>}
          {dailyRate && <p className="text-xs text-dark-green font-semibold mt-2" data-id="element-852">
              ${displayPrice} per day
            </p>}
        </div>
      </div>
    </motion.div>;
  // ─── Listing Needs More Info Screen ───
  if (listingStatus === 'needs-info') {
    return <div className="min-h-screen bg-cream pb-24" data-id="element-853">
        <ListingHeader data-id="element-854" />

        <div className="px-3 py-5" data-id="element-855">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.4
        }} className="flex flex-col items-center" data-id="element-856">
            {/* Minus Icon Circle */}
            <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 15,
            delay: 0.1
          }} className="w-20 h-20 rounded-full bg-clay/10 flex items-center justify-center mb-3" data-id="element-857">
              <MinusCircle size={40} className="text-clay" data-id="element-858" />
            </motion.div>

            {/* Status Text */}
            <h2 className="text-base font-bold text-charcoal" data-id="element-859">
              Listing Needs More Info
            </h2>
          </motion.div>

          {/* Request Message Card */}
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="mt-3 bg-clay/8 border border-clay/20 rounded-md p-3" data-id="element-860">
            <div className="flex items-start gap-2" data-id="element-861">
              <Info size={14} className="text-clay flex-shrink-0 mt-0.5" data-id="element-862" />
              <p className="text-xs text-charcoal/70 leading-relaxed" data-id="element-863">
                We need more information before approving your listing. Please
                submit information below.
              </p>
            </div>
          </motion.div>

          {/* Item Preview Card */}
          <ItemPreviewCard delay={0.3} data-id="element-864" />

          {/* Questions Section */}
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.4
        }} className="mt-3" data-id="element-865">
            <label className="block text-xs font-bold text-charcoal mb-2" data-id="element-866">
              Questions from Review
            </label>

            {/* Question Display Card */}
            <div className="bg-white border border-sand rounded-md p-3" data-id="element-867">
              <div className="flex items-start gap-2" data-id="element-868">
                <MessageSquare size={14} className="text-moss-gray flex-shrink-0 mt-0.5" data-id="element-869" />
                <p className="text-xs text-charcoal/80 leading-relaxed" data-id="element-870">
                  Is this car seat new? Is there any issues with it? When was it
                  last used?
                </p>
              </div>
            </div>

            {/* Response Text Area */}
            <textarea value={reviewResponse} onChange={e => setReviewResponse(e.target.value)} placeholder="Type response here..." className="w-full mt-2 p-2.5 border border-sand rounded text-charcoal text-xs placeholder-moss-gray focus:outline-none focus:ring-2 focus:ring-dark-green focus:border-transparent resize-y min-h-[100px]" data-id="element-871" />

            {/* Submit Button */}
            <div className="mt-3 pb-16" data-id="element-872">
              {responseSubmitted ? <motion.div initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} className="flex items-center justify-center gap-2 h-10 bg-dark-green/10 rounded-md" data-id="element-873">
                  <CheckCircle size={16} className="text-dark-green" data-id="element-874" />
                  <span className="text-sm font-medium text-dark-green" data-id="element-875">
                    Response submitted!
                  </span>
                </motion.div> : <Button variant="primary" fullWidth onClick={handleSubmitResponse} disabled={!reviewResponse.trim()} data-id="element-876">
                  Submit
                </Button>}
            </div>
          </motion.div>
        </div>
      </div>;
  }
  // ─── Listing Denied Screen ───
  if (listingStatus === 'denied') {
    return <div className="min-h-screen bg-cream pb-24" data-id="element-877">
        <ListingHeader data-id="element-878" />

        <div className="px-3 py-5" data-id="element-879">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.4
        }} className="flex flex-col items-center" data-id="element-880">
            {/* Denial Icon */}
            <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 15,
            delay: 0.1
          }} className="w-20 h-20 rounded-full bg-red-500/10 flex items-center justify-center mb-3" data-id="element-881">
              <XCircle size={40} className="text-red-500" data-id="element-882" />
            </motion.div>

            {/* Status Text */}
            <h2 className="text-base font-bold text-charcoal" data-id="element-883">
              Listing Denied
            </h2>
          </motion.div>

          {/* Reason Message Card */}
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="mt-3 bg-red-50 border border-red-200 rounded-md p-3" data-id="element-884">
            <div className="flex items-start gap-2" data-id="element-885">
              <AlertTriangle size={14} className="text-red-500 flex-shrink-0 mt-0.5" data-id="element-886" />
              <p className="text-xs text-charcoal/70 leading-relaxed" data-id="element-887">
                We are unable to approve your listing request due to your item
                falling outside the scope of AllHere's terms and conditions.
              </p>
            </div>
          </motion.div>

          {/* Item Preview Card */}
          <ItemPreviewCard delay={0.3} data-id="element-888" />

          {/* Action Buttons */}
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.4
        }} className="mt-3 space-y-2 pb-16" data-id="element-889">
            <Button variant="primary" fullWidth onClick={handleListAnother} data-id="element-890">
              <PlusCircle size={16} className="mr-2" data-id="element-891" />
              List Another Item
            </Button>

            <Button variant="secondary" fullWidth onClick={() => {
            if (onViewListings) {
              onViewListings();
            } else {
              onComplete();
            }
          }} data-id="element-892">
              <Package size={16} className="mr-2" data-id="element-893" />
              View my Listings
            </Button>

            <Button variant="secondary" fullWidth onClick={() => {
            if (onMyActivity) {
              onMyActivity();
            }
          }} data-id="element-894">
              <Activity size={16} className="mr-2" data-id="element-895" />
              View my Activity
            </Button>
          </motion.div>
        </div>
      </div>;
  }
  // ─── Listing Approved Screen ───
  if (listingStatus === 'approved') {
    return <div className="min-h-screen bg-cream pb-24" data-id="element-896">
        <ListingHeader data-id="element-897" />

        <div className="px-3 py-5" data-id="element-898">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.4
        }} className="flex flex-col items-center" data-id="element-899">
            {/* Checkmark Circle */}
            <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 15,
            delay: 0.1
          }} className="w-20 h-20 rounded-full bg-dark-green/15 flex items-center justify-center mb-3" data-id="element-900">
              <CheckCircle size={40} className="text-dark-green" data-id="element-901" />
            </motion.div>

            {/* Status Text */}
            <h2 className="text-base font-bold text-charcoal" data-id="element-902">
              Your Listing is Live!
            </h2>
          </motion.div>

          {/* Confirmation Message Card */}
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="mt-3 bg-dark-green/8 border border-dark-green/20 rounded-md p-3" data-id="element-903">
            <div className="flex items-start gap-2" data-id="element-904">
              <Sparkles size={14} className="text-dark-green flex-shrink-0 mt-0.5" data-id="element-905" />
              <p className="text-xs text-charcoal/70 leading-relaxed" data-id="element-906">
                Your listing was approved and is now live for borrowers to
                request!
              </p>
            </div>
          </motion.div>

          {/* Item Preview Card */}
          <ItemPreviewCard delay={0.3} data-id="element-907" />

          {/* Action Buttons */}
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.4
        }} className="mt-3 space-y-2 pb-16" data-id="element-908">
            <Button variant="primary" fullWidth onClick={() => {
            setListingStatus('form');
          }} data-id="element-909">
              <Calendar size={16} className="mr-2" data-id="element-910" />
              Edit Item Availability
            </Button>

            <Button variant="secondary" fullWidth onClick={() => {
            // In a real app, this would navigate to the specific listing detail
            if (onViewListings) {
              onViewListings();
            } else {
              onComplete();
            }
          }} data-id="element-911">
              <Eye size={16} className="mr-2" data-id="element-912" />
              View My Listing
            </Button>

            <Button variant="secondary" fullWidth onClick={() => {
            if (onViewListings) {
              onViewListings();
            } else {
              onComplete();
            }
          }} data-id="element-913">
              <Package size={16} className="mr-2" data-id="element-914" />
              My Listings
            </Button>

            <Button variant="secondary" fullWidth onClick={() => {
            if (onMyActivity) {
              onMyActivity();
            }
          }} data-id="element-915">
              <Activity size={16} className="mr-2" data-id="element-916" />
              My Activity
            </Button>
          </motion.div>
        </div>
      </div>;
  }
  // ─── Under Review Screen ───
  if (listingStatus === 'under-review') {
    return <div className="min-h-screen bg-cream pb-24" data-id="element-917">
        <ListingHeader data-id="element-918" />

        <div className="px-3 py-5" data-id="element-919">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.4
        }} className="flex flex-col items-center" data-id="element-920">
            {/* Checkmark Circle */}
            <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 15,
            delay: 0.1
          }} className="w-20 h-20 rounded-full bg-dark-green/10 flex items-center justify-center mb-3" data-id="element-921">
              <CheckCircle size={40} className="text-dark-green" data-id="element-922" />
            </motion.div>

            {/* Status Text */}
            <h2 className="text-base font-bold text-charcoal" data-id="element-923">
              Listing (under review)
            </h2>
          </motion.div>

          {/* Approval Notice Card */}
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="mt-3 bg-clay/8 border border-clay/20 rounded-md p-3" data-id="element-924">
            <Badge variant="warning" size="sm" data-id="element-925">
              <Clock size={10} className="mr-1" data-id="element-926" />
              Pending Approval
            </Badge>
            <p className="text-xs text-charcoal/70 leading-relaxed mt-2" data-id="element-927">
              If your item abides by AllHere's terms & conditions, it will be
              listed publicly for borrowers!
            </p>
          </motion.div>

          {/* Item Preview Card */}
          <ItemPreviewCard delay={0.3} data-id="element-928" />

          {/* Action Buttons */}
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.4
        }} className="mt-3 space-y-2 pb-16" data-id="element-929">
            <Button variant="primary" fullWidth onClick={handleListAnother} data-id="element-930">
              <PlusCircle size={16} className="mr-2" data-id="element-931" />
              List Another Item
            </Button>

            <Button variant="secondary" fullWidth onClick={() => {
            setListingStatus('form');
          }} data-id="element-932">
              <Calendar size={16} className="mr-2" data-id="element-933" />
              Edit Item Availability
            </Button>

            <Button variant="secondary" fullWidth onClick={() => {
            if (onViewListings) {
              onViewListings();
            } else {
              onComplete();
            }
          }} data-id="element-934">
              <Package size={16} className="mr-2" data-id="element-935" />
              View my Listings
            </Button>

            <Button variant="secondary" fullWidth onClick={() => {
            if (onMyActivity) {
              onMyActivity();
            }
          }} data-id="element-936">
              <Activity size={16} className="mr-2" data-id="element-937" />
              My Activity
            </Button>
          </motion.div>

          {/* Simulate notifications (prototype only) */}
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.8
        }} className="mt-4 flex flex-col items-center gap-1" data-id="element-938">
            <button onClick={() => setListingStatus('approved')} className="text-xs text-charcoal/30 underline underline-offset-2" data-id="element-939">
              Simulate approval notification →
            </button>
            <button onClick={() => setListingStatus('denied')} className="text-xs text-charcoal/30 underline underline-offset-2" data-id="element-940">
              Simulate denial notification →
            </button>
            <button onClick={() => setListingStatus('needs-info')} className="text-xs text-charcoal/30 underline underline-offset-2" data-id="element-941">
              Simulate needs more info notification →
            </button>
          </motion.div>
        </div>
      </div>;
  }
  // ─── Create Listing Form ───
  return <div className="min-h-screen bg-cream pb-24" data-id="element-942">
      <ListingHeader data-id="element-943" />

      {/* Form Content */}
      <div className="px-3 py-3 space-y-4" data-id="element-944">
        {/* Item Photos Section */}
        <div data-id="element-945">
          <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-946">
            Item Photos
          </label>
          <div className="flex flex-wrap gap-2" data-id="element-947">
            <AnimatePresence data-id="element-948">
              {photos.map((photo, index) => <motion.div key={photo} initial={{
              opacity: 0,
              scale: 0.8
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0,
              scale: 0.8
            }} className="relative w-[92px] h-[92px] rounded-md overflow-hidden" data-id="element-949">
                  <img src={photo} alt={`Upload ${index + 1}`} className="w-full h-full object-cover" data-id="element-950" />
                  <button onClick={() => handleRemovePhoto(index)} className="absolute top-1 right-1 p-1 bg-charcoal/80 rounded-full" data-id="element-951">
                    <X size={12} className="text-white" data-id="element-952" />
                  </button>
                </motion.div>)}
            </AnimatePresence>
            {photos.length < 5 && <motion.button onClick={() => setShowPhotoPicker(true)} whileTap={{
            scale: 0.95
          }} className="w-[92px] h-[92px] rounded-md border-2 border-dashed border-moss-gray flex flex-col items-center justify-center gap-1 text-moss-gray hover:border-dark-green hover:text-dark-green transition-colors" data-id="element-953">
                <Camera size={20} data-id="element-954" />
                <span className="text-[10px] font-medium" data-id="element-955">Add Photo</span>
              </motion.button>}
          </div>
          <p className="text-xs text-charcoal/60 mt-1.5" data-id="element-956">
            {photos.length} / 5 photos
          </p>
        </div>

        {/* Basic Information Section */}
        <div className="pt-2" data-id="element-957">
          <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-958">
            Basic Information
          </label>
          <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g., Professional Power Drill Set" className="w-full h-11 px-2.5 border border-sand rounded text-charcoal placeholder-moss-gray focus:outline-none focus:ring-2 focus:ring-dark-green focus:border-transparent" data-id="element-959" />
          <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Describe your item, its condition, and what's included..." rows={5} className="w-full mt-3 p-2.5 border border-sand rounded text-charcoal placeholder-moss-gray focus:outline-none focus:ring-2 focus:ring-dark-green focus:border-transparent resize-y min-h-[120px]" data-id="element-960" />
        </div>

        {/* Category Section */}
        <div className="pt-1" data-id="element-961">
          <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-962">
            Category
          </label>
          <div className="flex flex-wrap gap-x-1.5 gap-y-2" data-id="element-963">
            {categories.map(cat => {
            const isSelected = selectedCategories.includes(cat);
            return <button key={cat} onClick={() => handleCategoryToggle(cat)} className={`h-7 px-4 rounded-full text-xs font-medium transition-colors ${isSelected ? 'bg-dark-green text-white' : 'bg-sand/50 text-charcoal hover:bg-sand'}`} data-id="element-964">
                  {cat}
                </button>;
          })}
          </div>
          <p className="text-xs text-charcoal/50 mt-1.5" data-id="element-965">
            Select up to 2 categories
          </p>
        </div>

        {/* Pricing Section */}
        <div className="pt-2" data-id="element-966">
          <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-967">
            Set your daily rate
          </label>
          <div className="flex items-center gap-2" data-id="element-968">
            <div className="relative w-[120px]" data-id="element-969">
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-charcoal" data-id="element-970">
                $
              </span>
              <input type="number" value={dailyRate} onChange={e => setDailyRate(e.target.value)} placeholder="0.00" className="w-full h-11 pl-6 pr-2.5 border border-sand rounded text-charcoal placeholder-moss-gray focus:outline-none focus:ring-2 focus:ring-dark-green focus:border-transparent" data-id="element-971" />
            </div>
            {dailyRate && <span className="text-sm text-charcoal/60" data-id="element-972">per day</span>}
          </div>
          <p className="text-xs text-charcoal/60 mt-2" data-id="element-973">
            Price on listing: ${displayPrice}
          </p>
          <div className="mt-3" data-id="element-974">
            <div className="flex items-center gap-1.5 mb-2" data-id="element-975">
              <label className="text-sm font-medium text-charcoal" data-id="element-976">
                Deposit (hold)
              </label>
              <button onClick={() => setShowDepositInfo(true)} className="text-moss-gray hover:text-dark-green transition-colors" data-id="element-977">
                <Info size={16} data-id="element-978" />
              </button>
            </div>
            <div className="relative w-[120px]" data-id="element-979">
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-charcoal" data-id="element-980">
                $
              </span>
              <input type="number" value={deposit} onChange={e => setDeposit(e.target.value)} placeholder="0.00" className="w-full h-11 pl-6 pr-2.5 border border-sand rounded text-charcoal placeholder-moss-gray focus:outline-none focus:ring-2 focus:ring-dark-green focus:border-transparent" data-id="element-981" />
            </div>
          </div>
        </div>

        {/* Availability Section */}
        <div className="pt-4" data-id="element-982">
          <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-983">
            Let borrowers know which dates your item will NOT be available
          </label>
          <div className="bg-white rounded-md border border-sand overflow-hidden" data-id="element-984">
            <CalendarComponent selectedDates={blockedDates} onDateSelect={setBlockedDates} mode="multi" data-id="element-985" />
          </div>
        </div>

        {/* Submit Button */}
        <div className="pt-4 pb-16" data-id="element-986">
          <Button variant="primary" fullWidth size="lg" onClick={handleSubmit} disabled={!title || selectedCategories.length === 0 || photos.length === 0} data-id="element-987">
            Submit Listing for Review
          </Button>
        </div>
      </div>

      {/* Photo Picker Bottom Sheet */}
      <AnimatePresence data-id="element-988">
        {showPhotoPicker && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setShowPhotoPicker(false)} className="fixed inset-0 bg-black/40 z-[60]" data-id="element-989" />
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          damping: 30,
          stiffness: 300
        }} style={{
          bottom: '80px'
        }} className="fixed left-0 right-0 bg-white rounded-t-3xl z-[60] flex flex-col max-h-[calc(80vh-80px)]" data-id="element-990">
              <div className="flex items-center justify-between p-4 border-b border-sand flex-shrink-0" data-id="element-991">
                <h2 className="text-lg font-semibold text-charcoal" data-id="element-992">
                  Choose Photos
                </h2>
                <button onClick={() => setShowPhotoPicker(false)} className="p-2 hover:bg-sand rounded-full transition-colors" data-id="element-993">
                  <X size={20} className="text-charcoal" data-id="element-994" />
                </button>
              </div>
              <div className="px-4 pt-3 pb-2 flex-shrink-0" data-id="element-995">
                <p className="text-xs text-charcoal/60" data-id="element-996">
                  {remainingSlots > 0 ? `Select up to ${remainingSlots} more photo${remainingSlots !== 1 ? 's' : ''}` : 'Maximum photos reached'}
                </p>
              </div>
              <div className="overflow-y-auto px-4 pb-4 flex-1 min-h-0" data-id="element-997">
                <div className="grid grid-cols-3 gap-2" data-id="element-998">
                  {PLACEHOLDER_IMAGES.map(img => {
                const isSelected = photos.includes(img.url);
                const isDisabled = !isSelected && photos.length >= 5;
                return <motion.button key={img.url} whileTap={isDisabled ? {} : {
                  scale: 0.95
                }} onClick={() => !isDisabled && handleSelectPlaceholder(img.url)} className={`relative aspect-square rounded-lg overflow-hidden ${isDisabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`} data-id="element-999">
                        <img src={img.url} alt={img.label} className="w-full h-full object-cover" data-id="element-1000" />
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-1.5" data-id="element-1001">
                          <span className="text-[10px] text-white font-medium" data-id="element-1002">
                            {img.label}
                          </span>
                        </div>
                        {isSelected && <motion.div initial={{
                    scale: 0
                  }} animate={{
                    scale: 1
                  }} className="absolute inset-0 bg-dark-green/20 flex items-center justify-center" data-id="element-1003">
                            <div className="w-7 h-7 rounded-full bg-dark-green flex items-center justify-center shadow-md" data-id="element-1004">
                              <Check size={16} className="text-white" data-id="element-1005" />
                            </div>
                          </motion.div>}
                      </motion.button>;
              })}
                </div>
              </div>
              <div className="p-4 border-t border-sand flex-shrink-0" data-id="element-1006">
                <Button variant="primary" fullWidth onClick={() => setShowPhotoPicker(false)} data-id="element-1007">
                  Done ({photos.length}/5 selected)
                </Button>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>

      {/* Deposit Policy Popup */}
      <AnimatePresence data-id="element-1008">
        {showDepositInfo && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setShowDepositInfo(false)} className="fixed inset-0 bg-black/50 z-[300]" data-id="element-1009" />
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[90%] max-w-[400px] bg-white rounded-lg p-4 z-[300] shadow-xl" data-id="element-1010">
              <button onClick={() => setShowDepositInfo(false)} className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center hover:bg-sand/30 rounded-full transition-colors" data-id="element-1011">
                <X size={16} className="text-charcoal" data-id="element-1012" />
              </button>
              <h3 className="text-base font-bold text-charcoal pr-8" data-id="element-1013">
                Deposit (hold)
              </h3>
              <div className="mt-3 text-xs text-charcoal/80 leading-relaxed space-y-3" data-id="element-1014">
                <p data-id="element-1015">
                  Rather than dealing with insurance policies, if you lend an
                  item, a hold is placed on the borrower's credit card. This
                  amount is determined by you, the lender.
                </p>
                <p data-id="element-1016">
                  The deposit hold protects you in case the item is damaged,
                  lost, or not returned. If a claim is filed and substantiated
                  by our Support Team, the full or partial deposit is charged to
                  the borrower and given to you.
                </p>
                <p data-id="element-1017">
                  The hold is released automatically if no claim is filed within
                  7 days of return.
                </p>
                <p data-id="element-1018">
                  <button className="text-dark-green font-medium underline" data-id="element-1019">
                    Click HERE for full deposit policy
                  </button>
                </p>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </div>;
}